"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Sparkles,
  Heart,
  Target,
  Users,
  MessageSquare,
  Shield,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  Award,
  TrendingUp,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";
import { StepperBlock } from "@/lib/blocks/stepper-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "83.3", suffix: "%", label: "Campaign Success", sub: "15 victories / 18 engagements", accent: "coral" },
  { metric: "70", suffix: "%+", label: "Women in Teams", sub: "By selection, results", accent: "emerald" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years clean record", accent: "amber-gold" },
  { metric: "100", suffix: "%", label: "Client Satisfaction", sub: "Every engagement", accent: "red" },
];

const TRAITS = [
  { num: "01", title: "Rigor", body: "The ability to follow protocols and maintain standards under pressure. In sovereign-scale operations, rigor is the difference between 99.9999% uptime and a system failure that cascades across eighteen countries." },
  { num: "02", title: "Communication", body: "The willingness to surface problems early and clearly, before they escalate into crises. In environments where communication failures cost elections, the ability to say the uncomfortable thing is a survival skill." },
  { num: "03", title: "Risk Discipline", body: "The capacity to manage risk carefully rather than recklessly. To follow the systems that distinguish operational excellence from operational luck. Risk discipline produces the zero-security-incident record." },
  { num: "04", title: "Retention", body: "The likelihood of staying with the organization long enough to build institutional knowledge. Every departure costs one-half to two times the departing member&apos;s annual salary." },
  { num: "05", title: "Collaboration", body: "The ability to make the people around them better, not just to perform individually. In organizations where internal politics consumes up to forty percent of productive capacity, collaboration transforms operational performance." },
];

const ADVANTAGES = [
  { num: "01", title: "Communication", body: "Problems surface earlier. Team members are willing to say the uncomfortable thing in the room before it becomes a catastrophe in the boardroom.", accent: "red" },
  { num: "02", title: "Risk Discipline", body: "Protocols and systems followed meticulously. Risk is managed, not courted. The 99.9999% uptime standard is the product of this discipline.", accent: "purple" },
  { num: "03", title: "Collaboration", body: "Internal energy goes outward, toward the mission, instead of inward, toward each other. The teams compete against the problem, not against each other.", accent: "emerald" },
  { num: "04", title: "Retention", body: "Institutional knowledge preserved because the environment is one where people can stay and thrive. Every departure is a loss.", accent: "cyan" },
  { num: "05", title: "Ego Management", body: "Ego interferes less with execution. Operational systems function as designed, without the distortions that individual ego introduces.", accent: "amber-gold" },
];

const MOVEMENT = [
  { title: "The Operating Case", body: "A decade of sovereign-scale evidence. The argument is from operations, not morality. From outcomes, not aspirations. From verified data across eighteen countries, not a single case study." },
  { title: "The Selection System", body: "Optimize for the right signals — rigor, communication, risk discipline, retention, collaboration. The talent pool that emerges is the talent that outperforms. Not by design. By selection." },
  { title: "The Implementation Path", body: "Five steps: define the traits, redesign selection, design the environment, measure outcomes, iterate. A replicable framework refined through hundreds of team configurations and thousands of hiring decisions." },
];

export default function WomensEmpowermentPage() {
  return (
    <>
      <PageHero
        icon={Heart}
        overline="Women&apos;s Empowerment"
        overlineClassName="text-coral"
        title={
          <>
            The Operating Case, the Evidence, <span className="text-gradient-premium">the Movement.</span>
          </>
        }
        subtitle="Women&apos;s empowerment, as Dr. Jyoti Kush defines it, is not a philanthropic initiative, a corporate social responsibility checkbox, or a moral argument dressed in business language. It is an operational finding — the discovery that the world&apos;s most overlooked talent pool produces the most reliable operational outcomes when selection optimizes for the traits that actually determine whether teams deliver."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Women&apos;s Empowerment" }]}
        badge="The talent the world overlooks"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-5`}>
              <Sparkles className="size-3.5 text-coral" />
              <span className="text-overline text-coral">The Premise</span>
            </div>
            <h2 className="text-display-md mb-5">
              The world has a talent allocation problem — and the cost is measured in competitive advantage.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Organizations select leaders and team members on the wrong signals. Confidence is mistaken for competence. Volume is mistaken for capability. Self-promotion is mistaken for results. The loudest voice in the room gets promoted; the person who actually makes the team function gets overlooked.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The result is a systematic misallocation of talent. The cost is not abstract. It is measured in projects that fail, teams that fragment, institutional knowledge that hemorrhages with every turnover cycle, and competitive advantage that evaporates when the people who know how things actually work walk out the door.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Target className="size-3.5 text-coral" />
            <span className="text-overline text-coral">The Five Selection Traits</span>
          </div>
          <h2 className="text-display-md mb-4">Optimize for these. The talent pool emerges.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Optimize hiring for the five traits that produce reliable operational outcomes — and the talent pool that emerges is consistently majority-women.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {TRAITS.map((t, i) => (
            <motion.div
              key={t.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="relative glass-card rounded-2xl p-5 bg-gradient-to-br from-coral/10 to-transparent border border-coral/20"
            >
              <div className="text-display-sm font-bold text-coral/30 leading-none mb-3">{t.num}</div>
              <h3 className="text-heading-md mb-2 text-coral">{t.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Award className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Five Operating Advantages</span>
          </div>
          <h2 className="text-display-md mb-4">Why majority-female teams outperform.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Five observed, documented, and verified advantages — across a decade of sovereign-scale operations.
          </p>
        </div>
        <div className="space-y-4">
          {ADVANTAGES.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${ac.ring}`}>
                    <span className="text-display-sm font-bold text-white">{a.num}</span>
                  </div>
                  <div>
                    <h3 className={`text-display-sm mb-2 ${ac.text}`}>{a.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <MessageSquare className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Movement</span>
          </div>
          <h2 className="text-display-md mb-4">From evidence to movement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The 70% Project is the flagship initiative documenting the operating case — from evidence to movement, from documenting the operating case to building the infrastructure that makes it the default.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {MOVEMENT.map((m, i) => {
            const accents = ["red", "emerald", "purple"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{m.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{m.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="empowerment-pillars" ariaLabel="Empowerment Pillars">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Award className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Empowerment Pillars</span>
          </div>
          <h2 className="text-display-md mb-4">Seven pillars. One operating case for the world&apos;s most reliable talent pool.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature framework. Each pillar represents a domain where the operating case for the talent the world overlooks produces consistent, verified, sovereign-scale outcomes.
          </p>
        </div>
        <PillarGridBlock
          columns={4}
          variant="numbered"
          pillars={[
            { title: "Economic Empowerment", body: "Talent selection optimized for operational reliability produces teams that are over seventy percent women. The economic outcome is the operating outcome. Capability routed where it was systematically overlooked.", icon: <TrendingUp className="size-5" />, accent: "red" },
            { title: "Operational Excellence", body: "Eighty-three-point-three percent campaign success rate. Zero security incidents. Fifteen victories in eighteen engagements. The outcomes speak with the authority of verified data.", icon: <Award className="size-5" />, accent: "purple" },
            { title: "Talent Selection", body: "Selection optimized for the traits that predict operational reliability. Rigor, communication, risk discipline, retention, collaboration. The talent that emerges is the talent the world overlooks.", icon: <Target className="size-5" />, accent: "cyan" },
            { title: "Sovereign Operations", body: "Eighteen countries, three continents, fifteen-plus years. The teams that produce sovereign-scale outcomes are teams that the world was systematically mis-pricing.", icon: <Crown className="size-5" />, accent: "emerald" },
            { title: "Leadership Pipeline", body: "The 70% Operating Model produces leaders. Not by exclusion. By selection. The pipeline is the proof. The outcomes are the verification.", icon: <Users className="size-5" />, accent: "amber-gold" },
            { title: "Mentorship and Development", body: "Institutional knowledge preserved. Retention rates that exceed industry averages by multiples. The development infrastructure that makes the operating model sustainable.", icon: <BookOpen className="size-5" />, accent: "denim" },
            { title: "Movement and Advocacy", body: "From evidence to movement. The 70% Project as flagship initiative. The conversation reframe — from moral case to operating case. The data has done the work.", icon: <MessageSquare className="size-5" />, accent: "lime" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <MessageSquare className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Implementation Path</span>
          </div>
          <h2 className="text-display-md mb-4">Six steps from talent strategy to verified outcomes.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature vertical progression. The implementation path refined through hundreds of team configurations and thousands of hiring decisions. The discipline of the operating case, made replicable.
          </p>
        </div>
        <StepperBlock
          orientation="vertical"
          steps={[
            { num: "01", title: "Diagnose the Talent System", body: "Audit the current selection process. Identify the signals being optimized for. Map the gap between what the system rewards and what operational reliability requires.", icon: <Target className="size-4" />, accent: "red" },
            { num: "02", title: "Define the Operational Traits", body: "Rigor. Communication. Risk discipline. Retention. Collaboration. Each trait defined in operational terms — observable behaviors that can be assessed and verified.", icon: <Award className="size-4" />, accent: "purple" },
            { num: "03", title: "Redesign Selection", body: "Blind to gender and optimized for the five traits. Structured interviews with standardized scoring rubrics. Work simulations that test actual operational behavior.", icon: <Users className="size-4" />, accent: "cyan" },
            { num: "04", title: "Design the Environment", body: "Communication protocols. Risk management frameworks. Collaborative structures. Retention mechanisms. Outcome accountability. The environment must reinforce the five traits.", icon: <Shield className="size-4" />, accent: "emerald" },
            { num: "05", title: "Measure the Outcomes", body: "The operating model is validated by outcomes — team performance, retention, risk incidents, problem identification speed. Not by the composition of the team.", icon: <TrendingUp className="size-4" />, accent: "amber-gold" },
            { num: "06", title: "Iterate and Compound", body: "Every team built, every outcome measured, every lesson learned feeds back into the selection process. The clinical method applied to team building. The model compounds.", icon: <Sparkles className="size-4" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <BookOpen className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Movement Domains</span>
          </div>
          <h2 className="text-display-md mb-4">Six domains where the operating case produces outcomes.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The 70% Project extends across six distinct movement domains. Each domain represents a different surface for the operating case to be tested, validated, and propagated.
          </p>
        </div>
        <CardGridBlock
          columns={3}
          items={[
            { title: "Public Speaking", body: "Keynote presentations that translate the evidence into actionable leadership practice. Past stages include the Nobel Peace Prize, the World Economic Forum, and leadership forums globally.", icon: <Mic className="size-5" />, accent: "red" },
            { title: "Board Service", body: "Board and advisory board service for organizations committed to the operating case. The clinical discipline applied at the governance level.", icon: <Crown className="size-5" />, accent: "purple" },
            { title: "Executive Advisory", body: "Advisory for founders, executives, and institutions on the implementation of the 70% Operating Model. The case from operations, applied to organizational leadership.", icon: <Target className="size-5" />, accent: "cyan" },
            { title: "Network of Adopters", body: "A growing community of organizations that have adopted the model. Peer learning, shared measurement frameworks, collective advocacy for the operating case.", icon: <Users className="size-5" />, accent: "emerald" },
            { title: "The 70% Project Documentation", body: "The flagship initiative documenting the operating case with a decade of verified operational data. The evidence base. The implementation framework. The methodology.", icon: <BookOpen className="size-5" />, accent: "amber-gold" },
            { title: "Public Conversation", body: "The argument reframed. From diversity initiative to talent strategy. From moral case to operating case. The data has done the work — the conversation follows.", icon: <MessageSquare className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-coral/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-coral mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Join the operating case for the talent the world overlooks.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Keynotes, advisory, board service, and consulting engagements. The evidence is a decade of sovereign-scale outcomes. The argument is from operations, not morality.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Engage Dr. Jyoti Kush
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/the-70-percent-project/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Heart className="size-4" />
                The 70% Project
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Heart, accent: "coral" },
          { title: "Women in Leadership", description: "The operating case for the talent the world overlooks. A decade of verified evidence.", href: "/expertise/women-in-leadership/", icon: Users, accent: "emerald" },
          { title: "The 70% Project", description: "The flagship initiative documenting the operating case. From evidence to movement.", href: "/the-70-percent-project/", icon: Award, accent: "amber-gold" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "purple" },
        ]}
      />
    </>
  );
}
